// src/components/HeaderCount/StatisticsSection.jsx
import React, { useEffect, useState } from "react";
import { FaUsers, FaChalkboardTeacher, FaRegListAlt } from "react-icons/fa";
import { GiDiploma } from "react-icons/gi";

/* --- CountUp simple (sans lib externe) --- */
const useCountUp = (target = 0, duration = 800) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf, start;
    const from = 0;
    const to = Number(target || 0);
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / duration);
      setVal(Math.round(from + (to - from) * easeOut(p)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

/* --- Carte stat --- */
const StatCard = ({ icon: Icon, label, value, suffix, subtitle, loading }) => {
  const animated = useCountUp(loading ? 0 : value);
  if (loading) {
    return (
      <div className="rounded-2xl p-4 bg-white/85 dark:bg-zinc-900/70 backdrop-blur border border-white/30 dark:border-zinc-700/50 animate-pulse">
        <div className="h-6 w-6 rounded-lg bg-gray-200 dark:bg-zinc-800 mb-3" />
        <div className="h-4 w-1/2 bg-gray-200 dark:bg-zinc-800 rounded mb-2" />
        <div className="h-8 w-1/3 bg-gray-200 dark:bg-zinc-800 rounded" />
      </div>
    );
  }
  return (
    <div className="rounded-2xl p-4 bg-white/85 dark:bg-zinc-900/70 backdrop-blur border border-white/30 dark:border-zinc-700/50 shadow-sm hover:shadow-md transition-shadow duration-300">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/15 text-primary dark:text-white dark:bg-primary/30">
          <Icon className="text-xl" />
        </span>
        <div className="min-w-0">
          <p className="text-sm text-gray-600 dark:text-zinc-300 truncate">
            {label}
          </p>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-extrabold text-gray-900 dark:text-white tabular-nums">
              {animated}
            </span>
            {suffix && (
              <span className="text-lg font-bold text-gray-900 dark:text-white">
                {suffix}
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              {subtitle}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const StatisticsSection = ({
  title = "QUELQUES CHIFFRES",
  backgroundUrl = "/img/HeaderCount2.png",
  loading = false,
  licencies = 0,
  groupes = 0,
  enseignants = 0,
  inscritsEtCoursEssai = 0,
}) => {
  const total = Number(licencies) + Number(inscritsEtCoursEssai);
  const progress =
    total > 0 ? Math.round((Number(licencies) / total) * 100) : 0;

  return (
    <section
      className="relative text-white w-full bg-cover bg-center"
      style={{ backgroundImage: `url("${backgroundUrl}")` }}
      aria-label="Statistiques du club"
    >
      {/* Hauteur adaptable */}
      <div className="h-[340px] md:h-[420px] lg:h-[520px]" />

      {/* Overlay lisibilité */}
      <div className="absolute inset-0 bg-gradient-to-l from-black/65 via-black/45 to-transparent dark:from-black/75 dark:via-black/55" />

      {/* Zone de droite */}
      <div className="absolute inset-0">
        <div className="max-w-7xl mx-auto h-full px-4 sm:px-6 lg:px-8 flex justify-end">
          <div className="w-full md:w-1/2 lg:w-2/5 h-full flex flex-col justify-center">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-3 text-right uppercase tracking-wide text-shadow-[0_35px_35px_rgb(0_0_0_/_0.25)]">
              {title}
            </h2>

            {/* Grille des cartes */}
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <StatCard
                icon={FaUsers}
                label="Licenciés"
                value={licencies}
                subtitle="sur la saison en cours"
                loading={loading}
              />
              <StatCard
                icon={FaRegListAlt}
                label="Inscrits & essais"
                value={inscritsEtCoursEssai}
                subtitle="sur la saison en cours"
                loading={loading}
              />
              <StatCard
                icon={GiDiploma}
                label="Groupes"
                value={groupes}
                subtitle="Groupes actifs"
                loading={loading}
              />
              <StatCard
                icon={FaChalkboardTeacher}
                label="Enseignants"
                value={enseignants}
                subtitle="Diplômés & en formation"
                loading={loading}
              />
            </div>

            {/* Barre de progression — Licenciés vs Inscrits & essais */}
            <div className="mt-4 md:mt-5">
              <div className="flex justify-between items-center text-[11px] md:text-xs text-gray-200">
                <span className="opacity-100 rounded-full px-2">
                  <strong className="bg-green-500 rounded-full px-2 text-gray-900">Licenciés</strong> vs Inscrits & essais
                </span>
                <span className="font-medium tabular-nums">
                  {loading ? "…" : `${licencies} / ${total}`}
                </span>
              </div>

              <div
                className="mt-2 h-3.5 rounded-full bg-white/25 dark:bg-zinc-800/60 backdrop-blur border border-white/30 dark:border-zinc-700/50 overflow-hidden"
                role="progressbar"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Taux de licenciés"
                title={`${progress}% licenciés (${licencies}/${total})`}
              >
                <div
                  className="h-full rounded-full transition-[width] duration-700 ease-out shadow-[inset_0_0_8px_rgba(255,255,255,0.45)]"
                  style={{
                    width: loading ? "0%" : `${progress}%`,
                    background:
                      "linear-gradient(90deg, #065f46 0%, #16a34a 35%, #22c55e 68%, #4ade80 100%)", // vert foncé à vert clair
                  }}
                />
              </div>

              <div className="mt-2 text-right text-[11px] md:text-xs text-gray-200">
                {loading ? (
                  "…"
                ) : (
                  <span className="font-semibold">{progress}% licenciés</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatisticsSection;
